<script setup>
    import { markRaw, onMounted, onUnmounted, ref, defineAsyncComponent} from 'vue';

    import MainSiteLogo from './MainSiteLogo.vue';
    import MainMenuSingleItemComponent from './MainMenuSingleItemComponent.vue';
    import MainMenuHamburgerItemComponent from './MainMenuHamburgerItemComponent.vue';
    import SocialNetworkingComponent from '../../Helper/SocialNetworkingComponent.vue';
    import ButtonComponent from '@buttons/ButtonComponent.vue';
    import ModalMessageComponent from '@modals/ModalMessageComponent.vue';
    import PictureComponent from '@images/PictureComponent.vue';
    import {preparePictureSrcSets} from '@utils/preparePictureSrcSets';
    import {imageBreakpoints} from '@utils/imageBreakpoints';
    import ModalEditComponent from '@modals/ModalEditComponent.vue';
    import ModalVideoComponent from '@modals/ModalVideoComponent.vue';

    const props = defineProps({
        menuItems: [Array, Object],
        route: String,
        routeLogin: String,
        routeLogout: String,
        currentUrl: String,
        user: Object || null,
        enrolledCourses: Number || null,
    });

    const mobileBreakpointSmall = 640; // px

    const mobileBreakpoint = 1024; // px
    const mobileMenuState = ref(window.innerWidth > mobileBreakpoint);
    const smallMobileState = ref(window.innerWidth < mobileBreakpointSmall);

    function updateMenuState() {
        mobileMenuState.value = window.innerWidth > mobileBreakpoint;
        smallMobileState.value = window.innerWidth < mobileBreakpointSmall;
    }

    onMounted(() => {
        window.addEventListener('resize', updateMenuState);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', updateMenuState);
    });

    const toggleMenu = () => {
        document.body.classList.toggle('menu-open');
        mobileMenuState.value = !mobileMenuState.value;
    }

    const breakpoints = imageBreakpoints();
</script>

<template>
    <teleport to="#app">
        <ModalMessageComponent></ModalMessageComponent>
        <ModalEditComponent></ModalEditComponent>
        <ModalVideoComponent></ModalVideoComponent>
    </teleport>
    <div
        class="mx-auto w-full block relative lg:flex items-end justify-end lg:items-end lg:justify-between py-2 md:py-6 md:pt-12 border-b border-b-gray">
        <div class="flex items-center justify-between">
            <div class="mr-4">
                <main-site-logo></main-site-logo>
                <div class="text-gray-5 text-nowrap">Evidence Based Education</div>
            </div>
            <div class="flex items-center justify-end w-full lg:hidden">
                <main-menu-hamburger-item-component
                    @toggle-menu="toggleMenu"
                ></main-menu-hamburger-item-component>
            </div>
        </div>
        <div class="flex items-center justify-end lg:w-full lg:ms-auto">
            <nav v-if="mobileMenuState"
                id="menu"
                 class="fixed bg-white top-0 left-0 right-0 bottom-0 pt-28 lg:pt-0 w-full h-full md:w-full lg:w-auto m-0 md:mx-0 lg:bg-transparent lg:relative lg:top-0 lg:right-auto lg:bottom-auto lg:mx-0 z-50">
                <transition name="menu">
                    <ul class="lg:flex flex-wrap items-center pb-4 md:p-6 lg:p-0">
                        <li
                            class="relative text-right lg:text-center lg:font-bold text-2xl lg:text-lg text-blue-dark lg:py-2 lg:px-1" v-if="enrolledCourses">
                            <a
                                href="/my-courses"
                                class="flex relative w-full items-center justify-end lg:justify-start py-2 px-5 bg-white rounded-md hover:text-primary-1 transition-all duration-300"
                                :class="currentUrl === 'my-courses' ? 'current text-primary-1' : ''"
                            >
                                <span class="z-10">My Courses</span>
                            </a>
                        </li>
                        <main-menu-single-item-component
                            v-for="menuItem in menuItems"
                            :key="menuItem.id"
                            :menuItem="menuItem"
                            :current-url="currentUrl"
                        ></main-menu-single-item-component>
                        <li class="flex relative w-full lg:w-auto justify-end px-6 py-2 lg:py-0 lg:px-4">
                            <div
                                class="flex-none md:ml-6 w-16 scale-125 aspect-square rounded-full bg-gray flex text-center items-center justify-center text-white text-xs overflow-hidden"
                                 v-if="user">
                                <picture-component
                                    :alt="user.full_name"
                                    :default-src="user.srcSmall"
                                    :sources="preparePictureSrcSets(user.srcSets, breakpoints)"
                                    :img-class="'w-full h-full relative group-hover:grayscale transition duration-300 ease-in-out z-10'"
                                ></picture-component>
                            </div>
                            <button-component
                                v-else
                                title="Login"
                                :btn-class="'btn-primary inline-block w-auto'"
                                :text-size="'!px-6'"
                                :href="routeLogin"
                            ></button-component>
                        </li>
                    </ul>
                </transition>
            </nav>
        </div>
    </div>
</template>

<script>

</script>

<style scoped lang="scss">
.flex-60 {
    flex: 0 0 15rem;
}

/* Enter Transition */
.menu-enter-active,
.menu-leave-active {
    transition: opacity 0.5s, transform 0.5s;
    transform: translateX(0);
}

.menu-enter-from,
.menu-leave-to {
    opacity: 0;
    transform: translateX(100vw);
}
</style>
